import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { NewsService } from "../../services/news_service";
import config from "../../config/config";
import styles from "./NewsDetails.module.css";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaUser,
  FaClock,
  FaRegNewspaper,
  FaStar,
} from "react-icons/fa";
import { useNewsDetailState } from "../../states/useNewsDetailState";

const NewsDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const newsService = NewsService();
  const { news, setNews, loading, setLoading, error, setError } = useNewsDetailState();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchNewsDetails = async () => {
      try {
        const newsData = await newsService.fetchNewsById(id);
        setNews(newsData);
      } catch (error) {
        console.error("Error fetching news details", error);
        setError("News not found");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchNewsDetails();
    } else {
      setError("News ID is missing");
      setLoading(false);
    }
  }, [id]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return "/default-news.jpg";
    if (imagePath.startsWith("http")) return imagePath;
    return `${config.baseURL}${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      match: "#e74c3c",
      transfer: "#3498db",
      interview: "#9b59b6",
      analysis: "#f39c12",
      announcement: "#2ecc71"
    };
    return colors[category?.toLowerCase()] || "#e74c3c";
  };

  // Get full content without HTML tags
  const getFullContent = (content) => {
    if (!content) return "No content available.";
    return content.replace(/<[^>]*>/g, '');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading News...</p>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>News Not Found</h2>
          <p>The News you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/news')}
            className={styles.backButton}
          >
            <FaArrowLeft /> Back to News
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.newsDetailsPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          {news.image && (
            <img
              src={getFullImageUrl(news.image)}
              alt={news.title}
              className={styles.heroImage}
            />
          )}
        </div>
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
        >
          ← Back
        </button>
      </section>

      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            <div className={styles.mainContent}>
              <div className={styles.contentSection}>
                <div className={styles.newsContent}>
                  <h2>Article Overview</h2>
                  <h3>{news.title}</h3>
                  <div className={styles.newsText} dangerouslySetInnerHTML={{
                    __html: getFullContent(news.content),
                  }}>
                  </div>
                </div>

                {news.tags && news.tags.length > 0 && (
                  <div className={styles.tagsSection}>
                    <h3>Related Tags</h3>
                    <div className={styles.tagsList}>
                      {news.tags.map((tag, index) => (
                        <span key={index} className={styles.tag}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.sidebar}>
              <div className={styles.newsInfoCard}>
                <h3>Article Information</h3>

                <div className={styles.infoItem}>
                  <span className={styles.infoIcon}><FaRegNewspaper /></span>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Category</div>
                    <div className={styles.infoValue}>
                      {news.category}
                    </div>
                  </div>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoIcon}><FaCalendarAlt /></span>
                  <div className={styles.infoContent}>
                    <div className={styles.infoLabel}>Published</div>
                    <div className={styles.infoValue}>
                      {formatDate(news.published_at)}
                      <br />
                      {formatTime(news.published_at)}
                    </div>
                  </div>
                </div>
                {news.author && (

                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FaUser /></span>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Author</div>
                      <div className={styles.infoValue}>
                        {news.author}
                      </div>
                    </div>
                  </div>

                )}
                {news.is_featured && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoIcon}><FaStar /></span>
                    <div className={styles.infoContent}>
                      <div className={styles.infoLabel}>Status</div>
                      <div className={styles.infoValue}>
                        Featured Article
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetails;